// A comment is rendered as Markdown, where a single newline is not a line break. Every line is ended with
// `&nbsp; ` (a space, then the two trailing spaces Markdown reads as a hard break) so it shows as typed. Some
// lines are left alone, because the `&nbsp;` makes Markdown misread where a block starts or ends:
//
// - Code fences and the lines between them. A closing fence may be followed only by spaces, so ```` ```&nbsp; ````
//   did not close the block, everything after a code block was drawn inside it, and inside a block the text is
//   shown verbatim, so the `&nbsp;` itself appeared on every line.
// - The blank line after a quote. A quote runs on until a truly blank line, and `&nbsp; ` is not blank, so
//   everything after a quote was drawn inside its shaded box.
// - The blank line after a list, for the same reason: it is what stops the next line being drawn inside the last
//   item (see commentLists.ts).
// - A table, and the blank line after it. The `| --- |` row may hold only pipes, dashes, colons and spaces, so
//   with `&nbsp;` on the end the rows were drawn as one run-on paragraph; and a table runs on until a truly blank
//   line, so a blank line given `&nbsp;` would be drawn as one more row. Emailed tables arrive this way (#32435).

import { LIST_ITEM } from './commentLists';

const FENCE = /^ {0,3}(`{3,}|~{3,})/;
const QUOTE = /^ {0,3}>/;
const TABLE_DELIMITER_ROW = /^ {0,3}\|?[ \t]*:?-+:?[ \t]*(\|[ \t]*:?-+:?[ \t]*)*\|?\s*$/;

const isTableDelimiterRow = (line: string | undefined) => line !== undefined && line.includes('|') && TABLE_DELIMITER_ROW.test(line);

/**
 * A deflected answer saved on a ticket (DeflectedAnswerComment on the backend, deflectedAnswerComment.ts on the
 * portal): the catalog answer as the catalog editor saved it, where a blank line only separates lines and an empty
 * line typed for spacing is its own `&nbsp;` paragraph. Drawn with hardLineBreaks, every separator became a visible
 * line too, so each typed empty line showed as three or four.
 */
export const isDeflectedAnswer = (text: string): boolean => /^Deflected answer:\r?\n/.test(text ?? '');

export const hardLineBreaks = (text: string): string => {
  // Emailed comments were saved with \r\n. Split on \n alone, the \r stayed before the `&nbsp; `, and Markdown reads
  // a lone \r as a line break - so the `&nbsp;` drew as an empty line under every line (#33334).
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  let fenceOpen: string | null = null;
  let inQuote = false;
  let inList = false;
  let inTable = false;
  return lines.map((line, index) => {
    const fence = FENCE.exec(line)?.[1];
    if (fence) {
      if (fenceOpen === null) fenceOpen = fence;
      else if (fence.startsWith(fenceOpen[0]) && fence.length >= fenceOpen.length && line.trim() === fence) fenceOpen = null;
      return line;
    }
    if (fenceOpen !== null) return line;

    const blank = line.trim() === '';
    if (QUOTE.test(line)) inQuote = true;
    else if (blank && inQuote) {
      inQuote = false;
      return line;
    }
    if (LIST_ITEM.test(line)) inList = true;
    else if (line === '' && inList) {
      inList = false;
      return line;
    }
    else if (!/^[ \t]/.test(line)) inList = false;

    if (!inTable && line.includes('|') && isTableDelimiterRow(lines[index + 1])) inTable = true;
    if (inTable) {
      if (blank) inTable = false;
      return line;
    }

    if (index === lines.length - 1) return line;
    return `${line}&nbsp; `;
  }).join('\n');
};
